import { PlusOutlined } from '@ant-design/icons'
import { Button } from '@lmc/ui-kit'
import actions from 'actions'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import DocumentItem from './DocumentItem'
import { Document } from '@lmc/ts-common'
import { useParams } from 'react-router-dom'
import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'
import { LoadingScreen } from 'components/generic'
import { commonPatterns } from '../../../../../../utils/enhancedTestIds'

const MODAL_ID = 'documentModal'
const GET_DOCUMENTS_URL = '/api/list/documents'

type DocumentsInputProps = {
  onChange?: (value: string[]) => void
  value?: string[]
  disabled?: boolean
  testId?: string
}

const DocumentsInput = (props: DocumentsInputProps) => {
  const { onChange, value, disabled = false, testId } = props
  const { t } = useTranslation(NAMESPACE.GOALS)
  const baseId = testId || 'documents-input'

  const { id: residentID } = useParams()

  const [documents, setDocuments] = useState<Document[]>([])
  const [fetchedDocuments, setFetchedDocuments] = useState<Document[]>([])

  const {
    data: fetchedDocumentsResult,
    refetch: refetchDocuments,
    isLoading
  } = useQuery<{
    result: Document[]
  }>('documents', () =>
    getJSON({
      url: GET_DOCUMENTS_URL
    })
  )

  useEffect(() => {
    if (fetchedDocumentsResult?.result) {
      setFetchedDocuments(fetchedDocumentsResult?.result)
    }
  }, [fetchedDocumentsResult])

  const filterDocuments = () => {
    if (fetchedDocuments) {
      const newDocuments = mapDocumentsIDsToDocument(
        value || [],
        fetchedDocuments
      )

      setDocuments(newDocuments)
    }
  }

  useEffect(() => {
    filterDocuments()
  }, [value, fetchedDocuments])

  const dispatch = useDispatch()
  const openCreateModal = () => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: {
          listId: 'documents',
          type: 'create',
          onComplete: addDocument
        }
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        fields: { resident: residentID } as any
      })
    )
  }

  const editDocument = () => {
    refetchDocuments()
  }

  const openEditModal = (document: Document) => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: {
          listId: 'documents',
          type: 'edit',
          onComplete: editDocument
        }
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        initial: document
      })
    )
  }

  const unlink = (id: string) => {
    if (!value) {
      return
    }
    const newValue = value?.filter((element) => element !== id)

    onChange?.(newValue)
  }
  const addDocument = (document: Document) => {
    setFetchedDocuments((prevState) => [...prevState, document])

    const oldValue = value || []

    const newValue = [...oldValue, document.id]

    onChange?.(newValue)
  }

  const onDocumentClick = (url: string) => {
    window.open(url, '_blank')
  }

  return (
    <Wrapper data-testid={baseId}>
      {isLoading ? (
        <LoadingScreen data-testid={`${baseId}-loading`} />
      ) : (
        <ListWrapper data-testid={`${baseId}-list`}>
          {documents?.map((document, index) => (
            <DocumentItem
              key={document.id}
              disabled={disabled}
              onUnlink={() => unlink(document.id)}
              onEdit={() => openEditModal(document)}
              onClick={
                disabled
                  ? () =>
                      onDocumentClick(
                        document.originalFile as unknown as string
                      )
                  : undefined
              }
              hasBorder
              testId={`${baseId}-item-${index}`}
              {...document}
            />
          ))}
        </ListWrapper>
      )}
      {disabled || (
        <ButtonStyled
          style={{ alignSelf: 'flex-start' }}
          icon={<PlusOutlined />}
          variant="text"
          type="button"
          onClick={openCreateModal}
          data-testid={commonPatterns.button(baseId, 'add-document')}
        >
          {t(goalsConstants.ADD_DOCUMENT)}
        </ButtonStyled>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const ListWrapper = styled.div`
  margin-top: 8px;

  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ButtonStyled = styled(Button)`
  position: absolute;
  top: -30px;
  right: 0;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    position: static;
  }
`

const mapDocumentsIDsToDocument = (
  documentsIDs: string[],
  allDocumnets: Document[]
): Document[] => {
  const documents = allDocumnets.filter((document) =>
    documentsIDs.includes(document.id)
  )

  return documents
}

export default DocumentsInput
